import IdGeneratorService from '@/services/id-generator.service';
import { InputTextarea } from 'primereact/inputtextarea';
import { type Control, Controller, type FieldValues, type Path, RegisterOptions } from 'react-hook-form';
import { cn } from '../ui/cn';
import { useState } from 'react';
import { TextBodyExtraExtraSmall } from '../text/Texts';
import { Column } from '../container/Container';

type Props<T extends FieldValues> = {
	id: Path<T>;
	control: Control<T>;
	formName: string;
	label?: string;
	placeholder?: string;
	rows?: number;
	autoResize?: boolean;
	rules?: RegisterOptions<T, Path<T>>;
};

const CustomInputTextarea = <T extends FieldValues>({
	id,
	control,
	formName,
	label,
	placeholder,
	rows,
	autoResize,
	rules,
}: Props<T>) => {
	const idService = new IdGeneratorService(formName);
	const idGenerated = idService.generateFormInputIds(id);

	const [isFocus, setIsFocus] = useState(false);

	return (
		<Controller
			control={control}
			name={id}
			rules={rules}
			render={({ field, fieldState: { error } }) => {
				const hasValue = !!field.value;

				return (
					<Column className="relative">
						{/* Creating empty space for label in case we have more than x rows */}
						<div className="absolute top-0 left-0 right-0 h-6 bg-surfaceDim pointer-events-none" />
						<InputTextarea
							unstyled
							{...field}
							id={idGenerated.id}
							onFocus={() => setIsFocus(true)}
							onBlur={() => {
								setIsFocus(false);
								field.onBlur();
							}}
							autoResize={autoResize}
							rows={rows}
							className={cn(
								'w-full px-4 pt-6 rounded-t-[4px] bg-surfaceDim duration-200',
								'appearance-none focus:outline-none focus:ring-0 border-none shadow-none outline-none resize-none overflow-y-auto no-scrollbar',
								'font-poppins text-body-large text-onSurface',

								'bg-bottom bg-origin-border bg-no-repeat',
								'bg-[size:0_3px,_100%_1px]', isFocus && 'focus:bg-[size:100%_3px,_100%_1px]',
								error // error60, primary60, neutralVariant20 - colors
								? 'bg-[linear-gradient(to_bottom,#eb4236ff,#eb4236ff),linear-gradient(to_bottom,#eb4236ff,#eb4236ff)]'
								: 'bg-[linear-gradient(to_bottom,#004060ff,#004060ff),linear-gradient(to_bottom,#2f3639ff,#2f3639ff)]',
							)}
						/>

						{/* Placeholder */}
						{!hasValue && (<span
							className={cn(
								'absolute left-4 top-6',
								'font-poppins text-body-large text-onSurfaceVariant',
								'pointer-events-none transition-opacity duration-300',
								'opacity-0', isFocus && 'opacity-100'
							)}
							children={placeholder}
						/>)}

						{/* Label */}
						<label
							htmlFor={idGenerated.id}
							children={label ?? idService.generateLabel(id)}
							className={cn(
								'absolute z-10 origin-[0] left-4',
								'transition-all duration-300 pointer-events-none',
								'font-poppins text-body-large',
								error ? 'text-error60' : isFocus ? 'text-primary60' : 'text-neutralVariant20',
								(isFocus || hasValue) ? 'scale-[74%]' : 'translate-y-1/2 scale-100',
							)}
						/>

						{/* Error */}
						{error && (
							<TextBodyExtraExtraSmall
								id={`${idGenerated.id}-helper`}
								className="pl-4 pt-1 text-error"
								text={error?.message || ''}
							/>
						)}
					</Column>
				);
			}}
		/>
	);
};

export default CustomInputTextarea;
